import { PushNotifications } from '@capacitor/push-notifications';

import {
  NotificationPermissionDeniedError,
  NotificationRegistrationError,
  NotificationTokenTimeoutError,
} from '@/features/notifications/errors';
import { getPlatform } from '@/shared/utils/platform';
import { TimeoutError, withTimeout } from '@/shared/utils/promise';

const PUSH_REGISTRATION_TIMEOUT_MS = 30_000;

/**
 * Request permission for native push notifications (iOS/Android)
 */
export async function requestNativePermissions(): Promise<boolean> {
  const result = await PushNotifications.requestPermissions();
  return result.receive === 'granted';
}

/**
 * Check current native push notification permission status
 */
export async function checkNativePermissions(): Promise<boolean> {
  const result = await PushNotifications.checkPermissions();
  return result.receive === 'granted';
}

export interface GetNativeTokenOptions {
  /**
   * If true, only checks if permission is already granted (no popup).
   * If false (default), requests permission from user (shows system popup).
   */
  silent?: boolean;
}

/**
 * Get FCM token from native platform (APNs/FCM).
 *
 * @param options.silent - If true, only proceeds if permission already granted.
 *                         Use for background token refresh. Default: false.
 * @returns The FCM token.
 * @throws {NotificationPermissionDeniedError} If permission was denied.
 * @throws {NotificationTokenTimeoutError} If token registration timed out.
 * @throws {NotificationRegistrationError} If native registration failed.
 */
export async function getNativeToken(
  options?: GetNativeTokenOptions,
): Promise<string> {
  const permitted = options?.silent
    ? await checkNativePermissions()
    : await requestNativePermissions();

  if (!permitted) {
    throw new NotificationPermissionDeniedError(getPlatform());
  }

  try {
    return await withTimeout(
      new Promise<string>((resolve, reject) => {
        const registrationListener = PushNotifications.addListener(
          'registration',
          token => {
            void registrationListener.then(l => l.remove());
            void errorListener.then(l => l.remove());
            resolve(token.value);
          },
        );

        const errorListener = PushNotifications.addListener(
          'registrationError',
          error => {
            void registrationListener.then(l => l.remove());
            void errorListener.then(l => l.remove());
            reject(new NotificationRegistrationError(error.error));
          },
        );

        void PushNotifications.register();
      }),
      PUSH_REGISTRATION_TIMEOUT_MS,
    );
  } catch (error) {
    if (error instanceof TimeoutError) {
      throw new NotificationTokenTimeoutError(PUSH_REGISTRATION_TIMEOUT_MS);
    }
    throw error;
  }
}

/**
 * Remove all native push notification listeners
 */
export async function removeAllNativeListeners(): Promise<void> {
  await PushNotifications.removeAllListeners();
}
